import axios from 'axios';
import { API_ENDPOINTS } from './types';

export interface TemplateField {
  id: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  [key: string]: any;
}

export interface TemplateDTO {
  id: number;
  templateType: string;
  templateId: string;
  title: string;
  description: string;
  category: string;
  icon?: string;
  fields: TemplateField[];
}

export interface PaginatedTemplatesResponse {
  contents: TemplateDTO[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

class TemplateApiService {
  /**
   * Fetch paginated list of templates filtered optionally by type and category
   */
  async getTemplates(params?: {
    type?: string;
    category?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedTemplatesResponse> {
    try {
      const response = await axios.get(API_ENDPOINTS.TEMPLATES, {
        params: {
          type: params?.type || 'FORM',
          category: params?.category,
          page: params?.page || 0,
          size: params?.size || 10,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  /**
   * Fetch unique list of categories for a given template type
   */
  async getTemplateCategories(type?: string): Promise<string[]> {
    try {
      const response = await axios.get(`${API_ENDPOINTS.TEMPLATES}/categories`, {
        params: {
          type: type || 'FORM',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template categories:', error);
      throw error;
    }
  }

  /**
   * Fetch paginated list of templates for admin
   */
  async getAdminTemplates(params?: {
    type?: string;
    category?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedTemplatesResponse> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      const response = await axios.get(`${apiBase}/api/v1/admin/templates`, {
        params: {
          type: params?.type || 'FORM',
          category: params?.category,
          page: params?.page || 0,
          size: params?.size || 10,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin templates:', error);
      throw error;
    }
  }

  /**
   * Fetch unique list of categories for admin
   */
  async getAdminTemplateCategories(type?: string): Promise<string[]> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      const response = await axios.get(`${apiBase}/api/v1/admin/templates/categories`, {
        params: {
          type: type || 'FORM',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin template categories:', error);
      throw error;
    }
  }

  /**
   * Delete a template by its template ID (admin only)
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      await axios.delete(`${apiBase}/api/v1/admin/templates/${templateId}`);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  /**
   * Bulk delete templates by their template IDs (admin only)
   */
  async bulkDeleteTemplates(templateIds: string[]): Promise<{ deleted: number; requested: number }> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      const response = await axios.post(`${apiBase}/api/v1/admin/templates/bulk-delete`, {
        templateIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk deleting templates:', error);
      throw error;
    }
  }
}

const templateApiService = new TemplateApiService();
export default templateApiService;

export const getTemplates = templateApiService.getTemplates.bind(templateApiService);
export const getTemplateCategories = templateApiService.getTemplateCategories.bind(templateApiService);
export const getAdminTemplates = templateApiService.getAdminTemplates.bind(templateApiService);
export const getAdminTemplateCategories = templateApiService.getAdminTemplateCategories.bind(templateApiService);
export const deleteTemplate = templateApiService.deleteTemplate.bind(templateApiService);
export const bulkDeleteTemplates = templateApiService.bulkDeleteTemplates.bind(templateApiService);

